import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { departmentApi } from '../api/departmentApi'
import type { Department } from '../types/department'

export function useGetDepartments(params?: { buildingId?: string; floorId?: string }, enabled = true) {
  return useQuery({
    queryKey: ['departments', params],
    queryFn: () => departmentApi.getDepartments(params),
    enabled: enabled && (params === undefined || !!params.floorId),
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useGetDepartmentOptions(
  params?: { buildingId?: string; floorId?: string; excludeDepartmentId?: string; excludeWarehouse?: boolean },
  enabled = true,
) {
  return useQuery({
    queryKey: ['department-options', params],
    queryFn: () => departmentApi.getDepartmentOptions(params),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useGetDepartment(id: string, enabled = true) {
  return useQuery({
    queryKey: ['department', id],
    queryFn: () => departmentApi.getDepartment(id),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled && !!id,
  })
}

export function useGetDepartmentItems(
  id: string,
  params?: { status?: string; excludeOwnedProductItems?: boolean },
  enabled = true,
) {
  return useQuery({
    queryKey: ['department-items', id, params],
    queryFn: () => departmentApi.getDepartmentItems(id, params),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled && !!id,
  })
}

export function useCreateDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ payload }: { payload: Partial<Department> }) => departmentApi.createDepartment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] })
    },
  })
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Department> }) =>
      departmentApi.updateDepartment(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      queryClient.invalidateQueries({ queryKey: ['department'] })
    },
  })
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => departmentApi.deleteDepartment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] })
    },
  })
}
